import { FieldNumberOutlined, CalendarOutlined } from '@ant-design/icons';
import { Col, Row, Button, DatePicker, Select } from 'antd';
import { Formik, FormikHelpers } from 'formik';
import { Form, SubmitButton, Input } from 'formik-antd';
import moment, { Moment } from 'moment';
import 'moment/locale/ja';
import { getDataStoreFieldsMachine } from 'pages/ListMachine/thunk';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { MachineRequestState } from 'types';
import Wrap from './styles';

const { Option } = Select;

type Props = {
  onSearch: (value: any) => void;
};

const SearchMachine = ({ onSearch }: Props) => {
  const dispatch = useDispatch();
  const { searchValue } = useSelector((state: RootState) => state.machine);
  const [option, setOption] = useState<string>(searchValue?.lease_company ?? '');
  const [startDate, setStartDate] = useState<string>(searchValue?.startDate ?? '');
  const [endDate, setEndDate] = useState<string>(searchValue?.endDate ?? '');
  const [leaseDate, setLeaseDate] = useState<string>(searchValue?.leaseDate ?? '');
  const [quantityLeft, setQuantityLeft] = useState<string>(
    searchValue?.machine_quantity && searchValue?.machine_quantity?.length > 0 ? searchValue?.machine_quantity[0] : '',
  );
  const [quantityRight, setQuantityRight] = useState<string>(
    searchValue?.machine_quantity && searchValue?.machine_quantity?.length > 0 ? searchValue?.machine_quantity[1] : '',
  );

  const { dataStoreFields } = useSelector((state: RootState) => state.machine);

  useEffect(() => {
    dispatch(getDataStoreFieldsMachine({ appId: 'CPAP', dataStoreId: 't_equipment_lease' }));
  }, [dispatch]);

  const optionCompany = useMemo(() => {
    const result = dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'lease_company');
    if (!result) {
      return [];
    }
    return result[1].options;
  }, [dataStoreFields]);

  const onChangeStartDate = (value: any, dateString: string) => {
    setStartDate(dateString);
  };

  const onChangeEndDate = (value: any, dateString: string) => {
    setEndDate(dateString);
  };

  const onChangeLeaseDate = (value: any, dateString: string) => {
    setLeaseDate(dateString);
  };

  const validateStartDate = (dateString: Moment) => {
    return dateString && dateString > moment(endDate).startOf('day');
  };

  const validateEndDate = (dateString: Moment) => {
    return dateString && dateString < moment(startDate).endOf('day');
  };

  const handleSubmit = (values: MachineRequestState, form: FormikHelpers<MachineRequestState>) => {
    const dateStart = startDate && [
      `${moment(startDate, 'YYYY-MM-DD').startOf('day').format('YYYY-MM-DDTHH:mm:ssZ')}`,
      `${moment(startDate, 'YYYY-MM-DD').endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')}`,
    ];

    const dateEnd = endDate && [
      `${moment(endDate, 'YYYY-MM-DD').startOf('day').format('YYYY-MM-DDTHH:mm:ssZ')}`,
      `${moment(endDate, 'YYYY-MM-DD').endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')}`,
    ];

    const dateLease = leaseDate && [
      `${moment(leaseDate, 'YYYY-MM-DD').startOf('day').format('YYYY-MM-DDTHH:mm:ssZ')}`,
      `${moment(leaseDate, 'YYYY-MM-DD').endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')}`,
    ];
    const machineQuantity = (quantityLeft || quantityRight) && [`${quantityLeft}`, `${quantityRight}`];
    form.setSubmitting(false);
    onSearch({
      ...values,
      lease_company: option,
      lease_start_date: dateStart,
      lease_end_date: dateEnd,
      lease_contract_date: dateLease,
      machine_quantity: machineQuantity,
      startDate: startDate ?? '',
      endDate: endDate ?? '',
      leaseDate: leaseDate ?? '',
    });
  };

  const resetForm = (value: MachineRequestState) => {
    onSearch(undefined);
  };

  return (
    <Wrap>
      <Formik
        onSubmit={handleSubmit}
        initialValues={searchValue ? searchValue : initialValues}
        onReset={resetForm}
        render={() => (
          <Form>
            <Row className="detail">
              <Col span={15}>
                <Row>
                  <Col span={11} className="search">
                    <CalendarOutlined className="icon1 icon" />
                    <p className="standardBoldFont">リース期間</p>
                    <Form.Item name="startDate">
                      <DatePicker
                        name="startDate"
                        value={startDate ? moment(startDate) : undefined}
                        format={startDate ? 'YYYY年MM月DD日' : 'YYYY-MM-DDThh:mm:ssZ'}
                        onChange={onChangeStartDate}
                        disabledDate={validateStartDate}
                        locale={{
                          lang: {
                            locale: 'ja',
                            placeholder: 'Select date',
                            rangePlaceholder: ['Start date', 'End date'],
                            today: 'Today1',
                            now: 'Now',
                            backToToday: 'Back to today',
                            ok: 'Ok',
                            clear: 'Clear',
                            month: 'Month',
                            year: 'Year',
                            timeSelect: 'Select time',
                            dateSelect: 'Select date',
                            monthSelect: 'Choose a month',
                            yearSelect: 'Choose a year',
                            decadeSelect: 'Choose a decade',
                            yearFormat: 'YYYY',
                            dateFormat: 'M/D/YYYY',
                            dayFormat: 'D',
                            dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                            monthFormat: 'MMMM',
                            monthBeforeYear: true,
                            previousMonth: 'Previous month (PageUp)',
                            nextMonth: 'Next month (PageDown)',
                            previousYear: 'Last year (Control + left)',
                            nextYear: 'Next year (Control + right)',
                            previousDecade: 'Last decade',
                            nextDecade: 'Next decade',
                            previousCentury: 'Last century',
                            nextCentury: 'Next century',
                          },
                          timePickerLocale: {
                            placeholder: 'Select time',
                          },
                          dateFormat: 'YYYY-MM-DD',
                          dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                          weekFormat: 'YYYY-wo',
                          monthFormat: 'YYYY-MM',
                        }}
                        className="input-search-date"
                        placeholder=""
                        size={'large'}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={11} style={{ marginLeft: '5px' }} className="search">
                    <p className="standardBoldFont">〜</p>
                    <Form.Item name="endDate">
                      <DatePicker
                        name="endDate"
                        format={endDate ? 'YYYY年MM月DD日' : 'YYYY-MM-DDThh:mm:ssZ'}
                        value={endDate ? moment(endDate) : undefined}
                        onChange={onChangeEndDate}
                        disabledDate={validateEndDate}
                        locale={{
                          lang: {
                            locale: 'ja',
                            placeholder: 'Select date',
                            rangePlaceholder: ['Start date', 'End date'],
                            today: 'Today1',
                            now: 'Now',
                            backToToday: 'Back to today',
                            ok: 'Ok',
                            clear: 'Clear',
                            month: 'Month',
                            year: 'Year',
                            timeSelect: 'Select time',
                            dateSelect: 'Select date',
                            monthSelect: 'Choose a month',
                            yearSelect: 'Choose a year',
                            decadeSelect: 'Choose a decade',
                            yearFormat: 'YYYY',
                            dateFormat: 'M/D/YYYY',
                            dayFormat: 'D',
                            dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                            monthFormat: 'MMMM',
                            monthBeforeYear: true,
                            previousMonth: 'Previous month (PageUp)',
                            nextMonth: 'Next month (PageDown)',
                            previousYear: 'Last year (Control + left)',
                            nextYear: 'Next year (Control + right)',
                            previousDecade: 'Last decade',
                            nextDecade: 'Next decade',
                            previousCentury: 'Last century',
                            nextCentury: 'Next century',
                          },
                          timePickerLocale: {
                            placeholder: 'Select time',
                          },
                          dateFormat: 'YYYY-MM-DD',
                          dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                          weekFormat: 'YYYY-wo',
                          monthFormat: 'YYYY-MM',
                        }}
                        className="input-search-date"
                        placeholder=""
                        size={'large'}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={11} className="search">
                    <CalendarOutlined className="icon1 icon" />
                    <p className="standardBoldFont mr-37">契約日</p>
                    <Form.Item name="leaseDate">
                      <DatePicker
                        name="leaseDate"
                        format={leaseDate ? 'YYYY年MM月DD日' : 'YYYY-MM-DDThh:mm:ssZ'}
                        value={leaseDate ? moment(leaseDate) : undefined}
                        onChange={onChangeLeaseDate}
                        locale={{
                          lang: {
                            locale: 'ja',
                            placeholder: 'Select date',
                            rangePlaceholder: ['Start date', 'End date'],
                            today: 'Today',
                            now: 'Now',
                            backToToday: 'Back to today',
                            ok: 'Ok',
                            clear: 'Clear',
                            month: 'Month',
                            year: 'Year',
                            timeSelect: 'Select time',
                            dateSelect: 'Select date',
                            monthSelect: 'Choose a month',
                            yearSelect: 'Choose a year',
                            decadeSelect: 'Choose a decade',
                            yearFormat: 'YYYY',
                            dateFormat: 'M/D/YYYY',
                            dayFormat: 'D',
                            dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                            monthFormat: 'MMMM',
                            monthBeforeYear: true,
                            previousMonth: 'Previous month (PageUp)',
                            nextMonth: 'Next month (PageDown)',
                            previousYear: 'Last year (Control + left)',
                            nextYear: 'Next year (Control + right)',
                            previousDecade: 'Last decade',
                            nextDecade: 'Next decade',
                            previousCentury: 'Last century',
                            nextCentury: 'Next century',
                          },
                          timePickerLocale: {
                            placeholder: 'Select time',
                          },
                          dateFormat: 'YYYY-MM-DD',
                          dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                          weekFormat: 'YYYY-wo',
                          monthFormat: 'YYYY-MM',
                        }}
                        className="input-search-date"
                        placeholder=""
                        size={'large'}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={11} style={{ marginLeft: '5px' }} className="search mr-50">
                    <svg
                      className="icon1 icon"
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="0.5" y="0.5" width="14" height="14" fill="white" stroke="black" />
                      <rect x="2" y="2" width="2" height="2" fill="black" />
                      <rect x="2" y="5" width="2" height="2" fill="black" />
                      <rect x="2" y="8" width="2" height="2" fill="black" />
                      <rect x="5" y="2" width="2" height="2" fill="black" />
                      <rect x="5" y="5" width="2" height="2" fill="black" />
                      <rect x="5" y="8" width="2" height="2" fill="black" />
                      <rect x="5" y="11" width="5" height="3" fill="black" />
                      <rect x="8" y="2" width="2" height="2" fill="black" />
                      <rect x="8" y="5" width="2" height="2" fill="black" />
                      <rect x="8" y="8" width="2" height="2" fill="black" />
                      <rect x="11" y="2" width="2" height="2" fill="black" />
                      <rect x="11" y="5" width="2" height="2" fill="black" />
                      <rect x="11" y="8" width="2" height="2" fill="black" />
                    </svg>
                    <p className="standardBoldFont">リース会社</p>
                    <Form.Item style={{ width: '60%' }} name="lease_company">
                      <Select
                        className="input-search-date"
                        onChange={(e) => setOption(e)}
                        showArrow={false}
                        allowClear={true}
                        value={option}
                        size={'large'}
                      >
                        {optionCompany &&
                          optionCompany.map((g: any, i: number) => (
                            <Option key={i} value={g.display_id}>
                              {g.value}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={11} className="search">
                    <FieldNumberOutlined className="icon1 icon" />
                    <p className="standardBoldFont mr-23">契約番号</p>
                    <Form.Item name="lease_contract_id">
                      <Input size={'large'} name="lease_contract_id" className="input-search-date" />
                    </Form.Item>
                  </Col>
                  <Col span={11} style={{ marginLeft: '5px' }} className="search">
                    <p className="standardBoldFont">台数</p>
                    <Form.Item name="machine_quantity[0]">
                      <Input
                        className="input-search-unit"
                        name="machine_quantity[0]"
                        onChange={(e) => setQuantityLeft(e.target.value)}
                        placeholder=""
                        size={'large'}
                      />
                    </Form.Item>
                    <p className="standardBoldFont">〜</p>
                    <Form.Item name="machine_quantity[1]">
                      <Input
                        className="input-search-unit"
                        onChange={(e) => setQuantityRight(e.target.value)}
                        name="machine_quantity[1]"
                        placeholder=""
                        size={'large'}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col span={9} className="wrap-btn-search">
                <Button htmlType="reset" className="Standard backSetup">
                  リセット
                </Button>
                <SubmitButton className="btnSearch Standard btnDefault">絞り込み表示</SubmitButton>
              </Col>
            </Row>
          </Form>
        )}
      />
    </Wrap>
  );
};
const initialValues = {
  lease_start_date: [],
  lease_end_date: [],
  lease_contract_date: [],
  lease_company: '',
  lease_contract_id: '',
  machine_quantity: [],
  startDate: '',
  endDate: '',
  leaseDate: '',
  machineQuantity: [],
};

export default SearchMachine;
